import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, MessageCircle, Mail, Clock, CalendarDays, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { FIRM } from "@/lib/site";
import { useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const toISO = (d) => d.toISOString().split("T")[0];
const now = new Date();
const MIN_DATE = toISO(new Date(now.getTime() + 86400000));
const MAX_DATE = toISO(new Date(now.getTime() + 90 * 86400000));

const initialForm = {
  nome: "",
  email: "",
  telefono: "",
  area: "",
  messaggio: "",
  data_appuntamento: "",
  ora_appuntamento: "",
  consenso: false,
};

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: key === "consenso" ? !!e.target.checked : e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.nome.trim()) err.nome = t.contatti.errors.nome;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = t.contatti.errors.email;
    if (!form.area) err.area = t.contatti.errors.area;
    if (!form.data_appuntamento) err.data = t.contatti.errors.data;
    else {
      const d = new Date(`${form.data_appuntamento}T00:00:00`);
      if (Number.isNaN(d.getTime())) err.data = t.contatti.errors.data;
      else if (d.getDay() === 0 || d.getDay() === 6) err.data = t.contatti.errors.dataWeekend;
    }
    if (!form.ora_appuntamento) err.ora = t.contatti.errors.ora;
    if (form.messaggio.trim().length < 10) err.messaggio = t.contatti.errors.messaggio;
    if (!form.consenso) err.consenso = t.contatti.errors.consenso;
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(t.contatti.toasts.checkTitle, { description: t.contatti.toasts.checkDesc });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contatti.toasts.okTitle, { description: t.contatti.toasts.okDesc });
      setForm(initialForm);
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.contatti.toasts.failTitle, {
        description: typeof detail === "string" ? t.contatti.toasts.failDescFix : t.contatti.toasts.failDesc,
      });
    } finally {
      setSending(false);
    }
  };

  const inputCls = (err) =>
    `w-full border bg-alabaster/60 px-4 py-3.5 text-sm text-ink placeholder:text-muted-ink/50 transition-colors duration-300 focus:bg-white focus:outline-none ${
      err ? "border-red-400 focus:border-red-400" : "border-hairline focus:border-gold"
    }`;

  return (
    <section data-testid="contact-section" id="contatti" className="noise relative overflow-hidden bg-navy-deep py-24 text-alabaster lg:py-32">
      <div className="relative z-10 mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 lg:grid-cols-[1fr_1.2fr] lg:gap-24 lg:px-12">
        <div>
          <Reveal>
            <Overline light>{t.contatti.overline}</Overline>
            <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight sm:text-5xl">{t.contatti.title}</h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-alabaster/65">{t.contatti.intro}</p>
          </Reveal>

          <div className="mt-12 space-y-6">
            <Reveal delay={0.05}>
              <div data-testid="contact-info-address" className="flex items-start gap-4">
                <MapPin size={18} strokeWidth={1.25} className="mt-1 shrink-0 text-gold" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/50">{t.contatti.labels.studio}</p>
                  <p className="mt-1 text-sm leading-relaxed text-alabaster/90">{FIRM.address}</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="contact-info-phone" className="flex items-start gap-4">
                <Phone size={18} strokeWidth={1.25} className="mt-1 shrink-0 text-gold" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/50">{t.contatti.labels.phone}</p>
                  <a href={`tel:${FIRM.phoneHref}`} className="mt-1 block text-sm text-alabaster/90 transition-colors hover:text-gold">
                    {FIRM.phone}
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="contact-info-whatsapp" className="flex items-start gap-4">
                <MessageCircle size={18} strokeWidth={1.25} className="mt-1 shrink-0 text-gold" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/50">{t.contatti.labels.whatsapp}</p>
                  <a
                    href={FIRM.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-sm text-alabaster/90 transition-colors hover:text-gold"
                  >
                    {FIRM.whatsapp}
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div data-testid="contact-info-pec" className="flex items-start gap-4">
                <Mail size={18} strokeWidth={1.25} className="mt-1 shrink-0 text-gold" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/50">{t.contatti.labels.emailpec}</p>
                  <p className="mt-1 text-sm text-alabaster/90">{FIRM.email}</p>
                  <p className="text-sm text-alabaster/90">{FIRM.pec}</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex items-start gap-4">
                <Clock size={18} strokeWidth={1.25} className="mt-1 shrink-0 text-gold" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/50">{t.contatti.labels.hours}</p>
                  <p className="mt-1 text-sm text-alabaster/90">{t.topbar.hours}</p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.25}>
            <div data-testid="contact-map" className="mt-10 overflow-hidden border border-alabaster/12">
              <iframe
                title="Mappa della sede dello Studio"
                src="https://www.google.com/maps?q=Piazza+Vittorio+Emanuele+II+20c,+74024+Manduria+TA&output=embed"
                className="h-56 w-full opacity-90 grayscale-[0.65] contrast-[1.05] transition-opacity duration-500 hover:opacity-100"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="border border-alabaster/12 bg-white/[0.04] p-8 backdrop-blur-sm lg:p-10"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="nome" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/60">
                  {t.contatti.form.nome}
                </label>
                <input
                  id="nome"
                  data-testid="input-full-name"
                  type="text"
                  value={form.nome}
                  onChange={set("nome")}
                  className={inputCls(errors.nome)}
                  placeholder={t.contatti.form.nomePh}
                />
                {errors.nome && <p className="mt-1.5 text-xs text-red-300">{errors.nome}</p>}
              </div>
              <div>
                <label htmlFor="email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/60">
                  {t.contatti.form.email}
                </label>
                <input
                  id="email"
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls(errors.email)}
                  placeholder={t.contatti.form.emailPh}
                />
                {errors.email && <p className="mt-1.5 text-xs text-red-300">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="telefono" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/60">
                  {t.contatti.form.telefono}
                </label>
                <input
                  id="telefono"
                  data-testid="input-phone"
                  type="tel"
                  value={form.telefono}
                  onChange={set("telefono")}
                  className={inputCls(false)}
                  placeholder={t.contatti.form.telefonoPh}
                />
              </div>
              <div>
                <label htmlFor="area" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/60">
                  {t.contatti.form.area}
                </label>
                <select
                  id="area"
                  data-testid="select-practice-area"
                  value={form.area}
                  onChange={set("area")}
                  className={`${inputCls(errors.area)} appearance-none ${form.area ? "" : "text-muted-ink/50"}`}
                >
                  <option value="" disabled>{t.contatti.form.areaPh}</option>
                  {t.contatti.areas.map((a) => (
                    <option key={a} value={a} className="text-ink">{a}</option>
                  ))}
                </select>
                {errors.area && <p className="mt-1.5 text-xs text-red-300">{errors.area}</p>}
              </div>
              <div>
                <label htmlFor="data" className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/60">
                  <CalendarDays size={12} strokeWidth={1.5} className="text-gold" />
                  {t.contatti.form.data}
                </label>
                <input
                  id="data"
                  data-testid="input-date"
                  type="date"
                  min={MIN_DATE}
                  max={MAX_DATE}
                  value={form.data_appuntamento}
                  onChange={set("data_appuntamento")}
                  className={`${inputCls(errors.data)} [color-scheme:dark]`}
                />
                {errors.data && <p className="mt-1.5 text-xs text-red-300">{errors.data}</p>}
              </div>
              <div>
                <label htmlFor="ora" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/60">
                  {t.contatti.form.ora}
                </label>
                <select
                  id="ora"
                  data-testid="select-time"
                  value={form.ora_appuntamento}
                  onChange={set("ora_appuntamento")}
                  className={`${inputCls(errors.ora)} appearance-none ${form.ora_appuntamento ? "" : "text-muted-ink/50"}`}
                >
                  <option value="" disabled>{t.contatti.form.areaPh}</option>
                  {TIME_SLOTS.map((o) => (
                    <option key={o} value={o} className="text-ink">{o}</option>
                  ))}
                </select>
                {errors.ora && <p className="mt-1.5 text-xs text-red-300">{errors.ora}</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="messaggio" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-alabaster/60">
                  {t.contatti.form.messaggio}
                </label>
                <textarea
                  id="messaggio"
                  data-testid="input-message"
                  rows={4}
                  value={form.messaggio}
                  onChange={set("messaggio")}
                  className={`${inputCls(errors.messaggio)} resize-none`}
                  placeholder={t.contatti.form.messaggioPh}
                />
                {errors.messaggio && <p className="mt-1.5 text-xs text-red-300">{errors.messaggio}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    data-testid="checkbox-gdpr"
                    type="checkbox"
                    checked={form.consenso}
                    onChange={set("consenso")}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-alabaster/40 bg-transparent transition-colors checked:border-gold checked:bg-gold"
                  />
                  <span className="text-xs leading-relaxed text-alabaster/60">
                    {t.contatti.form.privacyPre}
                    <a
                      href="/#/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="privacy-link"
                      className="text-gold underline transition-colors hover:text-gold-dark"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t.contatti.form.privacyA}
                    </a>
                    {t.contatti.form.privacyPost}
                  </span>
                </label>
                {errors.consenso && <p className="mt-1.5 text-xs text-red-300">{errors.consenso}</p>}
              </div>
            </div>

            <button
              data-testid="submit-button"
              type="submit"
              disabled={sending}
              className="group relative mt-8 flex w-full items-center justify-center gap-3 overflow-hidden bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-navy transition-all duration-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? t.contatti.form.sending : t.contatti.form.submit}
              {!sending && (
                <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1" />
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
